/**
 * Google Maps Proxy Service
 * Uses Supabase Edge Function to proxy Google Maps API calls
 * This allows using Google Maps API with referer restrictions
 *
 * Fallback Strategy:
 * 1. Try Google Maps Proxy (via Supabase Edge Function)
 * 2. If proxy fails, use straight-line distance calculation
 *
 * Note: Direct Google Maps API calls are blocked by CORS in browsers
 */

import { SUPABASE_URL, SUPABASE_ANON_KEY } from './customSupabaseClient.js';

const METERS_PER_MILE = 1609.34;

function isPlaceholderEnv(value) {
  if (!value || typeof value !== 'string') return true;
  const v = value.trim().toLowerCase();
  return (
    !v ||
    v.includes('your_supabase') ||
    v.includes('your_google') ||
    v.includes('placeholder') ||
    v === 'undefined' ||
    v === 'null'
  );
}

function resolveSupabaseUrl() {
  const fromEnv = import.meta.env.VITE_SUPABASE_URL;
  if (!isPlaceholderEnv(fromEnv)) return fromEnv.replace(/\/$/, '');
  return SUPABASE_URL;
}

function resolveSupabaseAnonKey() {
  const fromEnv = import.meta.env.VITE_SUPABASE_ANON_KEY;
  if (!isPlaceholderEnv(fromEnv)) return fromEnv;
  return SUPABASE_ANON_KEY;
}

class GoogleMapsProxyService {
  constructor() {
    this.proxyUrl = '/functions/v1/google-maps-proxy';
    this.supabaseUrl = resolveSupabaseUrl();
    this.supabaseAnonKey = resolveSupabaseAnonKey();
    this.isProxyAvailable = false;

    this.suggestionsCache = new Map();
    this.placesCache = new Map();
    this.placeDetailsCache = new Map();
    this.geocodingCache = new Map();
    this.routingCache = new Map();

    this.testProxyAvailability();
  }

  getProxyHeaders() {
    if (import.meta.env.DEV) {
      return { 'Content-Type': 'application/json' };
    }

    return {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${this.supabaseAnonKey}`,
      apikey: this.supabaseAnonKey,
    };
  }

  canUseProxy() {
    if (import.meta.env.DEV) return true;
    return Boolean(this.supabaseUrl && this.supabaseAnonKey && !isPlaceholderEnv(this.supabaseUrl));
  }

  getProxyUrl() {
    if (import.meta.env.DEV) {
      return 'http://localhost:3003/api/google-maps-proxy';
    }
    return `${this.supabaseUrl}${this.proxyUrl}`;
  }

  async testProxyAvailability() {
    try {
      if (import.meta.env.DEV) {
        this.isProxyAvailable = true;
        return;
      }

      if (!this.canUseProxy()) {
        this.isProxyAvailable = false;
        return;
      }

      const response = await fetch(this.getProxyUrl(), {
        method: 'POST',
        headers: this.getProxyHeaders(),
        body: JSON.stringify({
          query: 'Washington DC',
          type: 'geocoding',
        }),
      });

      if (response.ok) {
        const data = await response.json();
        this.isProxyAvailable = data.status !== undefined;
      } else {
        this.isProxyAvailable = false;
        if (import.meta.env?.DEV) {
          console.warn('Google Maps proxy not available:', response.status, response.statusText);
        }
      }
    } catch (error) {
      this.isProxyAvailable = false;
      if (import.meta.env?.DEV) {
        console.warn('Google Maps proxy test failed:', error.message);
      }
    }
  }

  async getAddressSuggestions(query, countryCode = 'US') {
    if (!query || query.length < 2) return [];
    if (!this.canUseProxy()) return [];

    const cacheKey = `${query}-${countryCode}`;
    if (this.suggestionsCache.has(cacheKey)) {
      return this.suggestionsCache.get(cacheKey);
    }

    try {
      const response = await fetch(this.getProxyUrl(), {
        method: 'POST',
        headers: this.getProxyHeaders(),
        body: JSON.stringify({
          query,
          countryCode,
          type: 'geocoding',
        }),
      });

      if (!response.ok) {
        this.isProxyAvailable = false;
        if (import.meta.env?.DEV) {
          console.warn(`Google Maps proxy error: ${response.status} ${response.statusText}`);
        }
        return [];
      }

      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        this.isProxyAvailable = false;
        return [];
      }

      const data = await response.json();

      if (data.status === 'OK' && data.results && data.results.length > 0) {
        const suggestions = data.results.slice(0, 8).map((result, index) => ({
          place_id: `google_${result.place_id}`,
          description: result.formatted_address,
          formatted_address: result.formatted_address,
          lat: result.geometry.location.lat,
          lng: result.geometry.location.lng,
          score: 1.0 - index * 0.1,
          source: 'google',
        }));

        this.suggestionsCache.set(cacheKey, suggestions);
        return suggestions;
      }

      return [];
    } catch {
      this.isProxyAvailable = false;
      return [];
    }
  }

  /**
   * Google Places Autocomplete predictions (no lat/lng until place_details).
   */
  async getPlaceAutocomplete(query, countryCode = 'US') {
    if (!query || query.length < 2) return [];
    if (!this.canUseProxy()) return [];

    const cacheKey = `places:${query}-${countryCode}`;
    if (this.placesCache.has(cacheKey)) {
      return this.placesCache.get(cacheKey);
    }

    try {
      const response = await fetch(this.getProxyUrl(), {
        method: 'POST',
        headers: this.getProxyHeaders(),
        body: JSON.stringify({
          query,
          countryCode,
          type: 'places_autocomplete',
        }),
      });

      if (!response.ok) {
        if (import.meta.env?.DEV) {
          console.warn(`Places autocomplete error: ${response.status}`);
        }
        return [];
      }

      const data = await response.json();
      if (data.status !== 'OK' && data.status !== 'ZERO_RESULTS') {
        if (import.meta.env?.DEV) {
          console.warn('Places autocomplete status:', data.status, data.error_message);
        }
        return [];
      }

      const predictions = (data.predictions || []).slice(0, 8).map((prediction, index) => ({
        place_id: prediction.place_id,
        description: prediction.description,
        formatted_address: prediction.description,
        structured_formatting: prediction.structured_formatting,
        score: 0.95 - index * 0.05,
        source: 'google',
      }));

      this.placesCache.set(cacheKey, predictions);
      return predictions;
    } catch {
      return [];
    }
  }

  /**
   * Place Details — returns lat/lng + formatted address.
   */
  async getPlaceDetails(placeId) {
    if (!placeId) return null;
    if (!this.canUseProxy()) return null;

    // Strip optional google_ prefix from older geocoding ids
    const id = String(placeId).replace(/^google_/, '');

    if (this.placeDetailsCache.has(id)) {
      return this.placeDetailsCache.get(id);
    }

    try {
      const response = await fetch(this.getProxyUrl(), {
        method: 'POST',
        headers: this.getProxyHeaders(),
        body: JSON.stringify({
          type: 'place_details',
          placeId: id,
        }),
      });

      if (!response.ok) return null;

      const data = await response.json();
      if (data.status !== 'OK' || !data.result) return null;

      const result = data.result;
      const details = {
        place_id: result.place_id || id,
        description: result.formatted_address || result.name,
        formatted_address: result.formatted_address || result.name,
        name: result.name,
        lat: result.geometry?.location?.lat,
        lng: result.geometry?.location?.lng,
        source: 'google',
        score: 0.95,
      };

      this.placeDetailsCache.set(id, details);
      return details;
    } catch {
      return null;
    }
  }

  /**
   * Places Autocomplete + Details for suggestion list with coordinates.
   */
  async getPlaceSuggestionsWithDetails(query, countryCode = 'US') {
    const predictions = await this.getPlaceAutocomplete(query, countryCode);
    if (!predictions.length) return [];

    const detailed = await Promise.all(
      predictions.map(async (prediction, index) => {
        const details = await this.getPlaceDetails(prediction.place_id);
        if (!details || typeof details.lat !== 'number' || typeof details.lng !== 'number') {
          return null;
        }
        return {
          ...prediction,
          ...details,
          description: details.formatted_address || prediction.description,
          formatted_address: details.formatted_address || prediction.description,
          score: 0.95 - index * 0.05,
          source: 'google',
        };
      })
    );

    return detailed.filter(Boolean);
  }

  async geocodeAddress(address) {
    if (!address) return null;
    if (!this.canUseProxy()) return null;

    const cacheKey = address;
    if (this.geocodingCache.has(cacheKey)) {
      return this.geocodingCache.get(cacheKey);
    }

    try {
      const response = await fetch(this.getProxyUrl(), {
        method: 'POST',
        headers: this.getProxyHeaders(),
        body: JSON.stringify({
          query: address,
          type: 'geocoding',
        }),
      });

      if (!response.ok) {
        return null;
      }

      const data = await response.json();

      if (data.status === 'OK' && data.results && data.results.length > 0) {
        const result = data.results[0];
        const geocoded = {
          lat: result.geometry.location.lat,
          lng: result.geometry.location.lng,
          formatted_address: result.formatted_address,
          place_id: result.place_id,
          source: 'google',
        };

        this.geocodingCache.set(cacheKey, geocoded);
        return geocoded;
      }

      return null;
    } catch {
      return null;
    }
  }

  getLocationAddress(location) {
    if (!location) return null;
    return location.formatted_address || location.description || location.address || null;
  }

  getRouteCacheKey(origin, destination) {
    const originKey = this.getLocationAddress(origin) || `${origin.lat},${origin.lng}`;
    const destKey = this.getLocationAddress(destination) || `${destination.lat},${destination.lng}`;
    return `${originKey}|${destKey}`;
  }

  async calculateRouteDistance(origin, destination) {
    if (!origin || !destination) return null;

    const cacheKey = this.getRouteCacheKey(origin, destination);
    if (this.routingCache.has(cacheKey)) {
      return this.routingCache.get(cacheKey);
    }

    const validationError = this.validateRouteParameters(origin, destination);
    if (validationError) {
      console.warn('Invalid route parameters:', validationError);
      return this.calculateStraightLineDistance(origin, destination);
    }

    if (!this.canUseProxy()) {
      return this.calculateStraightLineDistance(origin, destination);
    }

    try {
      const originAddress = this.getLocationAddress(origin);
      const destinationAddress = this.getLocationAddress(destination);

      const requestBody = {
        origin: {
          lat: parseFloat(origin.lat),
          lng: parseFloat(origin.lng),
        },
        destination: {
          lat: parseFloat(destination.lat),
          lng: parseFloat(destination.lng),
        },
        originAddress,
        destinationAddress,
        type: 'directions',
      };

      if (!this.validateRequestBody(requestBody)) {
        return this.calculateStraightLineDistance(origin, destination);
      }

      const response = await fetch(this.getProxyUrl(), {
        method: 'POST',
        headers: this.getProxyHeaders(),
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        if (import.meta.env?.DEV) {
          console.warn('Google Maps proxy failed:', response.status, response.statusText);
        }
        return this.calculateStraightLineDistance(origin, destination);
      }

      const data = await response.json();

      if (data.status === 'OK' && data.routes && data.routes.length > 0) {
        const leg = data.routes[0].legs[0];
        const distanceMiles = leg.distance.value / METERS_PER_MILE;

        const result = {
          distance: distanceMiles,
          duration: leg.duration.value / 60,
          distanceText: leg.distance.text,
          durationText: leg.duration.text,
          source: 'google',
        };

        this.routingCache.set(cacheKey, result);
        return result;
      }

      return this.calculateStraightLineDistance(origin, destination);
    } catch {
      return this.calculateStraightLineDistance(origin, destination);
    }
  }

  async calculateDirectGoogleMapsDistance(origin, destination) {
    return this.calculateStraightLineDistance(origin, destination);
  }

  calculateStraightLineDistance(origin, destination) {
    const R = 3959;
    const dLat = ((destination.lat - origin.lat) * Math.PI) / 180;
    const dLng = ((destination.lng - origin.lng) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos((origin.lat * Math.PI) / 180) *
        Math.cos((destination.lat * Math.PI) / 180) *
        Math.sin(dLng / 2) *
        Math.sin(dLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const distance = R * c;
    const duration = distance * 2;

    return {
      distance,
      duration,
      distanceText: `${distance.toFixed(1)} mi (straight-line)`,
      durationText: `${Math.round(duration)} min (estimated)`,
      source: 'straight-line-fallback',
    };
  }

  validateRouteParameters(origin, destination) {
    if (!origin || !destination) {
      return 'Origin and destination are required';
    }

    if (typeof origin.lat !== 'number' || typeof origin.lng !== 'number') {
      return 'Origin coordinates must be numbers';
    }

    if (typeof destination.lat !== 'number' || typeof destination.lng !== 'number') {
      return 'Destination coordinates must be numbers';
    }

    if (origin.lat < -90 || origin.lat > 90 || destination.lat < -90 || destination.lat > 90) {
      return 'Latitude must be between -90 and 90 degrees';
    }

    if (origin.lng < -180 || origin.lng > 180 || destination.lng < -180 || destination.lng > 180) {
      return 'Longitude must be between -180 and 180 degrees';
    }

    const distance = this.calculateStraightLineDistance(origin, destination).distance;
    if (distance < 0.001) {
      return 'Origin and destination are too close';
    }
    if (distance > 12400) {
      return 'Distance too large (over 12,400 miles)';
    }

    return null;
  }

  validateRequestBody(requestBody) {
    if (!requestBody || typeof requestBody !== 'object') {
      return false;
    }

    if (!requestBody.origin || !requestBody.destination || !requestBody.type) {
      return false;
    }

    if (typeof requestBody.origin.lat !== 'number' || typeof requestBody.origin.lng !== 'number') {
      return false;
    }

    if (typeof requestBody.destination.lat !== 'number' || typeof requestBody.destination.lng !== 'number') {
      return false;
    }

    if (requestBody.type !== 'directions' && requestBody.type !== 'geocoding' &&
        requestBody.type !== 'places_autocomplete' && requestBody.type !== 'place_details') {
      return false;
    }

    return true;
  }

  resetProxyStatus() {
    this.isProxyAvailable = true;
  }
}

export default new GoogleMapsProxyService();
